/** "Edit" drawer of a template card: version picker, every knob of the template's params_schema grouped
 * (caption / motion / plate, locked ones shown read-only), a debounced live preview over the clip's own plate
 * (POST /api/clips/{id}/try → ONE 9:16 still at the first line-settle with the edited params, polled until done;
 * never a video), its download, and "Save as preset". Nothing here renders: the edited params are what Choose / OK
 * sends for this template (POST …/renders lives in ChooseView only). */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef, useState } from "react";
import { mediaDownloadUrl, mediaUrl } from "../../api/client";
import { qk } from "../../api/keys";
import { useAnnounce } from "../../components/announce";
import { errorText } from "../common";
import type { ParamSpec, TemplateOut, TryOut } from "../types";
import { listTries, savePreset, tryTemplate } from "./api";
import { asCanvas, asPalette } from "./clipLanes";
import { latestOf, PaletteView } from "./PaletteCard";
import {
  AUTO, autoNumber, coerce, displayValue, effective, GROUP_LABELS, groups, newSeed, overrides, presetId, resolvedNumbers,
  resolvedValues, stable, startNumber, type Params,
} from "./params";

export const PREVIEW_DEBOUNCE_MS = 600;
export const AUTO_PREVIEW_MS = 1500;
export const POLL_MS = 2000;

function Resolved({ id, text }: { id: string; text?: string }) {
  return text ? <span className="ws8-muted" data-testid={`${id}-resolved`}> = {text} (this clip)</span> : null;
}

/** A number knob that also takes `auto` (plate height / pan / centre y): an "auto" checkbox plus a number field
 * (and slider) within the schema's min / max. Typing is free; an out-of-range number is clamped on blur. */
function AutoNumberKnob({ spec, value, onSet, id, resolved, resolvedNum }: {
  spec: ParamSpec; value: unknown; onSet: (v: unknown) => void; id: string; resolved?: string; resolvedNum?: number;
}) {
  const isAuto = value === AUTO || typeof value !== "number";
  const num = typeof value === "number" ? value : startNumber(spec, resolvedNum);
  const [draft, setDraft] = useState(String(num));
  useEffect(() => setDraft(String(num)), [num]);
  const lo = spec.min ?? undefined;
  const hi = spec.max ?? undefined;
  const typed = (raw: string) => {
    setDraft(raw);
    const n = Number(raw);
    if (raw.trim() !== "" && Number.isFinite(n) && (lo == null || n >= lo) && (hi == null || n <= hi)) {
      const v = coerce(spec, raw);
      if (v != null) onSet(v);
    }
  };
  return (
    <div className="ws8-knob" data-testid={id} title={spec.help ?? undefined}>
      <span>{spec.label}</span>{" "}
      <label>
        <input type="checkbox" checked={isAuto} data-testid={`${id}-auto`} aria-label={`${spec.label}: auto`}
          onChange={(e) => onSet(e.target.checked ? AUTO : startNumber(spec, resolvedNum))} />
        {" "}auto
      </label>
      {isAuto ? (
        <>{" "}<span className="ws8-badge">{AUTO}</span><Resolved id={id} text={resolved} /></>
      ) : (
        <>
          {" "}
          <input type="range" min={lo ?? 0} max={hi ?? 1} step={spec.step ?? (spec.type === "int" ? 1 : 0.01)} value={num}
            aria-label={`${spec.label} slider`} data-testid={`${id}-range`}
            onChange={(e) => { const v = coerce(spec, e.target.value); if (v != null) onSet(v); }} />
          <input type="number" aria-label={`${spec.label} value`} data-testid={`${id}-value`} className="ws8-num"
            min={lo} max={hi} step={spec.step ?? "any"} value={draft}
            onChange={(e) => typed(e.target.value)}
            onBlur={() => { const v = coerce(spec, draft); if (v != null) onSet(v); setDraft(String(v ?? num)); }} />
          {lo != null && hi != null && <span className="ws8-muted"> {lo}–{hi}</span>}
        </>
      )}
    </div>
  );
}

function Knob({ spec, value, onSet, idBase, resolved, resolvedNum }: {
  spec: ParamSpec; value: unknown; onSet: (v: unknown) => void; idBase: string; resolved?: string; resolvedNum?: number;
}) {
  const id = `${idBase}-${spec.key}`;
  const title = spec.help ?? undefined;
  const fixed = spec.locked || spec.type === "const";
  if (!fixed && autoNumber(spec)) {
    return <AutoNumberKnob spec={spec} value={value} onSet={onSet} id={id} resolved={resolved} resolvedNum={resolvedNum} />;
  }
  if (fixed || (value === AUTO && spec.type !== "enum")) {
    return (
      <div className="ws8-knob" data-testid={id} title={title}>
        <span>{spec.label}</span> <span className="ws8-badge">{displayValue(fixed ? spec.default : value)}</span>
        {(fixed ? spec.default : value) === AUTO && <Resolved id={id} text={resolved} />}
      </div>
    );
  }
  if (spec.type === "bool") {
    return (
      <label className="ws8-knob" title={title}>
        <input type="checkbox" checked={value === true} onChange={(e) => onSet(e.target.checked)} data-testid={id} />
        {" "}{spec.label}
      </label>
    );
  }
  if (spec.type === "enum") {
    return (
      <label className="ws8-knob" title={title}>
        {spec.label}{" "}
        <select value={String(value ?? "")} onChange={(e) => onSet(e.target.value)} data-testid={id}>
          {(spec.enum ?? []).map((o) => <option key={o} value={o}>{o}</option>)}
        </select>
        {value === AUTO && <Resolved id={id} text={resolved} />}
      </label>
    );
  }
  if (spec.type === "color") {
    return (
      <label className="ws8-knob" title={title}>
        {spec.label}{" "}
        <input type="color" value={String(value ?? "#ffffff")} onChange={(e) => onSet(e.target.value.toUpperCase())}
          data-testid={id} />
      </label>
    );
  }
  if (spec.type === "colors") {
    const list = Array.isArray(value) ? (value as string[]) : [];
    const max = spec.max ?? 8;
    const min = spec.min ?? 1;
    return (
      <fieldset className="ws8-knob" title={title} data-testid={id}>
        <legend>{spec.label}</legend>
        {list.map((c, i) => (
          <span key={i} className="ws8-row">
            <input type="color" aria-label={`${spec.label} ${i + 1}`} value={c}
              onChange={(e) => onSet(list.map((x, j) => (j === i ? e.target.value.toUpperCase() : x)))} />
            <button type="button" aria-label={`Remove ${spec.label} ${i + 1}`} disabled={list.length <= min}
              onClick={() => onSet(list.filter((_, j) => j !== i))}>×</button>
          </span>
        ))}
        <button type="button" disabled={list.length >= max} onClick={() => onSet([...list, "#FFFFFF"])}>
          Add colour
        </button>
      </fieldset>
    );
  }
  const num = typeof value === "number" ? value : Number(spec.default ?? 0);
  return (
    <div className="ws8-knob" title={title}>
      <label htmlFor={id}>{spec.label}</label>{" "}
      <input id={id} type="range" min={spec.min ?? 0} max={spec.max ?? 1} step={spec.step ?? (spec.type === "int" ? 1 : 0.01)}
        value={num} onChange={(e) => onSet(coerce(spec, e.target.value) ?? num)} data-testid={id} />
      <input type="number" aria-label={`${spec.label} value`} min={spec.min ?? undefined} max={spec.max ?? undefined}
        step={spec.step ?? "any"} value={num} className="ws8-num"
        onChange={(e) => { const v = coerce(spec, e.target.value); if (v != null) onSet(v); }} />
    </div>
  );
}

function TryResult({ t, title }: { t: TryOut; title: string }) {
  if (t.status === "queued" || t.status === "running") {
    return <p className="ws8-muted" data-testid="try-status" role="status">Preview #{t.id} {t.status}…</p>;
  }
  if (t.status === "failed") {
    return <p className="ws8-badge ws8-badge-bad" data-testid="try-status" role="status">Preview #{t.id} failed: {t.error ?? "unknown error"}</p>;
  }
  return (
    <div data-testid="try-result">
      <p className="ws8-muted" data-testid="try-status" role="status">
        Preview #{t.id} ready{t.problems.length ? ` with ${t.problems.length} problem${t.problems.length === 1 ? "" : "s"}` : ""}
      </p>
      {t.problems.length > 0 && (
        <ul className="ws8-problems" data-testid="try-problems">{t.problems.map((p) => <li key={p}>{p}</li>)}</ul>
      )}
      <div className="ws8-try-stills">
        {t.still_media_ids.map((m, i) => {
          const at = `${(t.still_times[i] ?? 0).toFixed(2)} s`;
          return (
            <figure key={`${m}-${i}`} className="ws8-try-frame" data-testid="try-still">
              <a href={mediaDownloadUrl(m)} download aria-label={`Download ${title} preview still ${i + 1}`}>
                <img src={mediaUrl(m)} alt={`${title} preview still ${i + 1} at ${at}`} />
              </a>
              <figcaption className="ws8-muted">
                at {at} · <a href={mediaDownloadUrl(m)} download>Download still{t.still_media_ids.length > 1 ? ` ${i + 1}` : ""}</a>
              </figcaption>
            </figure>
          );
        })}
      </div>
    </div>
  );
}

export function TemplateEditor({ clipId, template, defaults, params, onChange }: {
  clipId: number; template: TemplateOut; defaults: Record<string, unknown>; params: Params;
  onChange: (p: Params) => void;
}) {
  const announce = useAnnounce();
  const qc = useQueryClient();
  const schema = template.params_schema;
  const presets = template.presets;
  const values = effective(schema, presets, params, defaults);
  const pid = presetId(params, defaults);
  const idBase = `knob-${template.id}`;
  const [tryId, setTryId] = useState<number | null>(null);
  const [auto, setAuto] = useState(false);
  const [name, setName] = useState("");
  const [err, setErr] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const triesKey = qk.list("tries", { clipId, template: template.id });

  const tries = useQuery({
    queryKey: triesKey,
    queryFn: () => listTries(clipId, template.id),
    enabled: tryId != null,
    refetchInterval: (q) => {
      const t = (q.state.data ?? []).find((x) => x.id === tryId);
      return t && (t.status === "queued" || t.status === "running") ? POLL_MS : false;
    },
  });
  const current = (tries.data ?? []).find((x) => x.id === tryId) ?? null;
  const latest = current ?? latestOf(tries.data);  // the auto values are shown resolved from this try
  const resolved = resolvedValues(latest);
  const resolvedNum = resolvedNumbers(latest);
  const latestCanvas = asCanvas(latest?.canvas);
  const latestPalette = asPalette(latest?.palette);

  const set = (key: string, v: unknown) => onChange(overrides(schema, presets, { ...params, [key]: v }, defaults));
  const pickPreset = (id: string) => onChange(id ? { preset: id } : {});

  const sendPreview = async (p: Params = params) => {
    try {
      const t = await tryTemplate(clipId, template.id, p);
      setErr(null);
      setTryId(t.id);
      qc.setQueryData<TryOut[]>(triesKey, (old) => [t, ...(old ?? []).filter((x) => x.id !== t.id)]);
      announce(`Preview #${t.id} of ${template.title} ${t.status === "done" ? "ready (cached)" : "queued"}`);
    } catch (e) {
      setErr(errorText(e));
      announce(`Preview failed: ${errorText(e)}`, "error");
    }
  };
  const requestPreview = (delay = PREVIEW_DEBOUNCE_MS, p?: Params) => {
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(() => void sendPreview(p), delay);
  };
  /** A new random variant: a fresh `seed` param (jitters canvas position, swing, blur), previewed at once. */
  const reroll = () => {
    const next = overrides(schema, presets, { ...params, seed: newSeed() }, defaults);
    onChange(next);
    announce(`${template.title}: new variant, seed ${String(next.seed)}`);
    requestPreview(0, next);
  };
  useEffect(() => () => { if (timer.current) clearTimeout(timer.current); }, []);
  const sig = stable(params);
  const first = useRef(true);
  useEffect(() => {
    if (first.current) {
      first.current = false;
      return;
    }
    if (auto) requestPreview(AUTO_PREVIEW_MS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sig, auto]);

  const save = async () => {
    try {
      const p = await savePreset(template.id, name.trim(), pid ? { ...params, preset: params.preset ?? pid } : params);
      setErr(null);
      announce(`Saved preset ${p.id} of ${template.title}`);
      onChange({ preset: p.id });
      setName("");
      void qc.invalidateQueries({ queryKey: qk.entity("clip", clipId, "template-options") });
    } catch (e) {
      setErr(errorText(e));
      announce(`Save failed: ${errorText(e)}`, "error");
    }
  };

  return (
    <section className="ws8-editor" aria-label={`Edit ${template.title}`} data-testid={`editor-${template.id}`}>
      {presets.length > 0 && (
        <label className="ws8-knob">
          Version{" "}
          <select value={typeof params.preset === "string" ? params.preset : ""} data-testid={`${idBase}-preset`}
            onChange={(e) => pickPreset(e.target.value)}>
            <option value="">Default for this clip{defaults.preset ? ` (${String(defaults.preset)})` : ""}</option>
            {presets.map((p) => (
              <option key={p.id} value={p.id}>{p.title}{p.source === "user" ? " (saved)" : ""}</option>
            ))}
          </select>
        </label>
      )}
      {pid && <p className="ws8-muted">{presets.find((p) => p.id === pid)?.description}</p>}
      {groups(schema).map((g) => (
        <fieldset key={g.group} className="ws8-knobs" data-testid={`${idBase}-group-${g.group}`}>
          <legend>{GROUP_LABELS[g.group] ?? g.group}</legend>
          {g.specs.map((s) => (
            <Knob key={s.key} spec={s} value={values[s.key]} onSet={(v) => set(s.key, v)} idBase={idBase}
              resolved={resolved[s.key]} resolvedNum={resolvedNum[s.key]} />
          ))}
        </fieldset>
      ))}
      {(latestCanvas || latestPalette || latest?.seed != null) && (
        <div className="ws8-knobs" data-testid={`${idBase}-resolved`}>
          <p className="ws8-muted">
            Resolved on this clip by preview #{latest?.id}
            {latestCanvas ? ` · canvas: plate ${(latestCanvas.plate_pct ?? latestCanvas.plate_h_px / 19.2).toFixed(1)}% (${
              latestCanvas.plate_h_px} px), crop_x ${latestCanvas.crop_x.toFixed(3)}, plate_cy ${latestCanvas.plate_cy.toFixed(3)}` : ""}
            {latest?.seed != null ? ` · seed ${latest.seed}` : ""}
          </p>
          {latestPalette && <PaletteView p={latestPalette} />}
        </div>
      )}
      <p className="ws8-muted" data-testid={`${idBase}-summary`}>
        {Object.keys(params).length ? `Sends: ${stable(params)}` : "Sends no params (the default version)"}
      </p>
      <div className="ws8-row" role="group" aria-label={`${template.title} preview actions`}>
        <button type="button" onClick={() => requestPreview()} data-testid={`${idBase}-preview`}>Preview</button>
        <button type="button" onClick={reroll} data-testid={`${idBase}-reroll`}
          aria-label={`Reroll ${template.title}: new random variant with a new seed`}>Reroll</button>
        <span className="ws8-muted" data-testid={`${idBase}-seed`}>
          Seed {typeof params.seed === "number" ? params.seed : latest?.seed ?? "drawn by the server"}
        </span>
        <label><input type="checkbox" checked={auto} onChange={(e) => setAuto(e.target.checked)} data-testid={`${idBase}-auto`} /> Auto-preview</label>
        <button type="button" onClick={() => onChange({})} disabled={!Object.keys(params).length} data-testid={`${idBase}-reset`}>Reset</button>
        <label>
          Preset name{" "}
          <input value={name} onChange={(e) => setName(e.target.value.toLowerCase())} pattern="[a-z0-9][a-z0-9_-]*"
            maxLength={40} data-testid={`${idBase}-name`} />
        </label>
        <button type="button" onClick={() => void save()} disabled={!/^[a-z0-9][a-z0-9_-]{0,39}$/.test(name.trim())}
          data-testid={`${idBase}-save-preset`}>
          Save as preset
        </button>
      </div>
      {err && <p className="ws8-badge ws8-badge-bad" role="alert">{err}</p>}
      {current && <TryResult t={current} title={template.title} />}
    </section>
  );
}
